import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import { ArrowForward, CheckRounded } from '@material-ui/icons';
import MaterialPhoneInput from '@orientaction/components/MaterialPhoneInput';
import {
  TURNSTILE_TOKEN_FIELD,
  TurnstileWidget,
  useTurnstile,
} from '@orientaction/components/Turnstile';
import { UserProfilePhotoContext } from '@orientaction/Context';
import { useResponseUser } from '@orientaction/hooks';
import { userService } from '@orientaction/services';
import { emailMatch, publicIconPath } from '@orientaction/utils';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { ChangeEvent, FC, useContext, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import config from '../../../../services/config';
import { connectUser } from '../../../../services/user.service';
import { claimShareResourcesAfterAuth } from '../../../../utils/claimAuthorizedResource';
import { apiServiceNative } from '../../../../utils/fetchNative';
import { getAppleConnectUrl } from '../../../../utils/sso';
import AccountConfirm from '../../../Authentication/Commons/AccountConfirm';
import HelpModal from '../../Auth/components/HelpModal';
import loginLocale from '../../Auth/Login/locale.json';
import signupLocale from '../../Auth/Signup/locale.json';
import RedesignImage from '../components/RedesignImage';
import { useInlineSignupStyles } from './index.styles';

interface IProps {
  testImage?: string;
  responses?: any;
  defaultEmail?: string;
  testId?: string;
  ebookResultId?: string;
  ebookIndex?: string;
  gameResultId?: string;
  authorizedGameId?: number | string;
  authorizedEbookId?: number | string;
  onGameLinked?: () => void;
  contentType?: 'test' | 'ebook' | 'game';
}

const PASSWORD_REGEX =
  /^(((?=.*[!@#$%^&*])(?=.*[A-Z])(?=.*[a-z]))|((?=.*\d)(?=.*[A-Z])(?=.*[a-z]))|((?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z]))|((?=.*\d)(?=.*[!@#$%^&*])(?=.*[A-Z]))).{8,}$/;

const deleteLead = async (email: string, tId?: string) => {
  if (!email || !tId) return;
  try {
    const emailParam = encodeURIComponent(email);
    const url = `/api/leads?filters[email][$eq]=${emailParam}&filters[test][id][$eq]=${tId}`;
    const res = await apiServiceNative.get(url);
    const items = res?.data || res;
    if (Array.isArray(items) && items.length > 0) {
      await Promise.all(items.map((item: any) => apiServiceNative.delete(`/api/leads/${item.id}`)));
    }
  } catch {
    // silent
  }
};

const InlineSignup: FC<IProps> = ({
  responses,
  defaultEmail = '',
  testId,
  ebookResultId,
  ebookIndex = '0',
  gameResultId,
  authorizedGameId,
  authorizedEbookId,
  onGameLinked,
  contentType = 'test',
}) => {
  const cls = useInlineSignupStyles();
  const history = useHistory();
  const { search } = useLocation();
  const { postResponseUserConnected } = useResponseUser();
  const { resetPhoto, updateUserProfilePhoto } = useContext<any>(UserProfilePhotoContext);
  const isEbook = contentType === 'ebook' && !!ebookResultId;
  const isGame = contentType === 'game' && !!gameResultId;

  const resumeSharedEbook = async () => {
    if (!ebookResultId) return;
    const linked = await apiServiceNative.get(`/api/ebook-front-user/${ebookResultId}`);
    return linked?.id || ebookResultId;
  };

  const linkGameResultToUser = async (userId: number) => {
    if (!gameResultId) return;
    await apiServiceNative.put(`/api/update-result-game/${gameResultId}`, {
      user_id: userId,
      forBrevo: true,
    });
  };

  const continueEbookReading = (readingId: string | number = ebookResultId || '') => {
    history.push(`/ebook/view/${readingId}/${ebookIndex}`);
  };

  const continueGamePlaying = () => {
    if (onGameLinked) {
      onGameLinked();
      return;
    }
    history.push(`/game/play/${gameResultId}/0`);
  };

  const claimSharedResource = async (userId?: number, token?: string) => {
    // The guest ebook result already records the share acquisition. Link that
    // same result after authentication so its answers stay with the reader.
    if (isEbook) return;
    if (!userId || (!isEbook && !isGame)) {
      return;
    }
    try {
      await claimShareResourcesAfterAuth({
        userId,
        token,
        gameId: isGame ? authorizedGameId : undefined,
        ebookId: isEbook ? authorizedEbookId : undefined,
      });
    } catch {
      // Attribution is best-effort and must not block login/signup.
    }
  };

  const [view, setView] = useState<'signup' | 'login'>('signup');
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resultId, setResultId] = useState<string | null>(null);
  const [showAccountConfirm, setShowAccountConfirm] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [smsError, setSmsError] = useState('');

  const [loginForm, setLoginForm] = useState({ email: defaultEmail, password: '' });
  const [loginErrors, setLoginErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState({
    lastname: '',
    firstname: '',
    email: defaultEmail,
    password: '',
    confirmPassword: '',
    cgu: false,
  });
  const [step2, setStep2] = useState({ phone: '', postal_code: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const turnstile = useTurnstile();

  const handleChange = (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [field]: e.target.value });
    setErrors({ ...errors, [field]: '' });
  };

  const validateStep1 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.lastname.trim()) errs.lastname = 'Champ obligatoire';
    if (!form.firstname.trim()) errs.firstname = 'Champ obligatoire';
    if (!form.email.trim()) errs.email = 'Champ obligatoire';
    else if (!emailMatch.test(form.email)) errs.email = 'Veuillez saisir une adresse e-mail valide';
    if (!form.password) errs.password = 'Champ obligatoire';
    else if (!PASSWORD_REGEX.test(form.password))
      errs.password =
        'Au moins 8 caractères avec 3 des éléments : minuscules, majuscules, chiffres, spéciaux';
    if (!form.confirmPassword) errs.confirmPassword = 'Champ obligatoire';
    else if (form.password !== form.confirmPassword)
      errs.confirmPassword = "Le mot de passe n'est pas identique";
    if (!form.cgu) errs.cgu = "Veuillez accepter les conditions générales d'utilisation";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStep1Submit = async () => {
    if (!validateStep1()) return;

    const turnstileToken = turnstile.getTokenOrError();
    if (!turnstileToken) return;

    setIsLoading(true);
    try {
      await userService.verifyIfMailExist(form.email, turnstileToken);
      turnstile.reset();
      setStep(2);
    } catch {
      turnstile.reset();
      setErrors({
        ...errors,
        email:
          "Cette adresse e-mail est déjà utilisée. Veuillez vous connecter ou changer d'adresse.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateStep2 = (): boolean => {
    const errs: Record<string, string> = {};
    if (!step2.phone || step2.phone.length <= 2) errs.phone = 'Champ obligatoire';
    else if (!isValidPhoneNumber(step2.phone)) errs.phone = 'Le format du numéro est invalide';
    if (!step2.postal_code) errs.postal_code = 'Champ obligatoire';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStep2Submit = async () => {
    if (!validateStep2()) return;

    const turnstileToken = turnstile.getTokenOrError();
    if (!turnstileToken) return;

    setIsLoading(true);
    try {
      const from = new URLSearchParams(search).get('from');
      const registerData = await userService.registerUser({
        ...form,
        ...step2,
        language: 1,
        fromTest: from === 'test',
        company: 1,
        username: form.email,
        authorized_games: isGame ? [authorizedGameId] : undefined,
        shared_ebook_id: isEbook ? authorizedEbookId : undefined,
        [TURNSTILE_TOKEN_FIELD]: turnstileToken,
      });

      if (registerData?.data?.user?.confirmed === false) {
        setShowAccountConfirm(true);
        if (isEbook) {
          await apiServiceNative.post(
            '/api/users-permissions/user/set-post-confirmation-redirect',
            {
              userId: registerData?.data?.user.id,
              redirectTo: `/ebook/view/${ebookResultId}/${ebookIndex}`,
            }
          );
        } else if (isGame) {
          await linkGameResultToUser(registerData?.data?.user.id);
          await apiServiceNative.post(
            '/api/users-permissions/user/set-post-confirmation-redirect',
            {
              userId: registerData?.data?.user.id,
              redirectTo: `/game/play/${gameResultId}/0`,
            }
          );
        } else if (responses) {
          deleteLead(form.email || defaultEmail, testId);
          const dataToPost = {
            ...responses,
            users_permissions_users: [registerData?.data?.user.id],
          };
          const datareturn = await postResponseUserConnected(dataToPost, registerData?.data?.jwt);
          await apiServiceNative.post(
            '/api/users-permissions/user/set-post-confirmation-redirect',
            {
              userId: registerData?.data?.user.id,
              redirectTo: `/congrats/${datareturn.id}`,
            }
          );
        } else {
          history.push('/main');
        }
      }
    } catch {
      turnstile.reset();
      setErrors({ global: 'Une erreur est survenue. Veuillez réessayer.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestSms = async () => {
    if (!step2.phone) return;
    setSmsError('');
    try {
      const result = await apiServiceNative.post('/api/users-permissions/user/send-sms-code', {
        phone: step2.phone,
      });
      history.push({
        pathname: '/verify-phone',
        state: { phone: step2.phone, retryAfterSeconds: result?.retryAfterSeconds },
      });
    } catch {
      setSmsError("Une erreur est survenue lors de l'envoi du SMS. Veuillez réessayer.");
    }
  };

  const handleLoginSubmit = async () => {
    const errs: Record<string, string> = {};
    if (!loginForm.email.trim()) errs.email = 'Champ obligatoire';
    else if (!emailMatch.test(loginForm.email))
      errs.email = 'Veuillez saisir une adresse e-mail valide';
    if (!loginForm.password) errs.password = 'Champ obligatoire';
    setLoginErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setIsLoading(true);
    setLoginErrors({});
    try {
      resetPhoto();
      const connectData = await connectUser({
        identifier: loginForm.email,
        password: loginForm.password,
      });
      if (connectData?.data?.jwt) {
        localStorage.setItem('userInfos', JSON.stringify(connectData?.data?.user));
        localStorage.setItem('userToken', connectData?.data?.jwt);
        updateUserProfilePhoto();
        deleteLead(loginForm.email || defaultEmail, testId);
        await claimSharedResource(connectData?.data?.user.id, connectData?.data?.jwt);

        if (isEbook) {
          const readingId = await resumeSharedEbook();
          continueEbookReading(readingId);
        } else if (isGame) {
          await linkGameResultToUser(connectData?.data?.user.id);
          continueGamePlaying();
        } else if (responses) {
          const dataToPost = {
            ...responses,
            users_permissions_users: [connectData?.data?.user.id],
          };
          const datareturn = await postResponseUserConnected(dataToPost, connectData?.data?.jwt);
          setResultId(datareturn.id);
        } else {
          history.push('/main');
        }
      }
    } catch (error: any) {
      if (error?.response?.data?.error?.name === 'ValidationError') {
        setLoginErrors({
          global: 'Vérifiez que vous avez rentré correctement vos identifiants.',
        });
      } else {
        setLoginErrors({ global: 'Une erreur est survenue. Veuillez réessayer.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderPasswordToggle = () => (
    <button
      type="button"
      className={cls.inputIcon}
      aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
      onClick={() => setShowPassword(prev => !prev)}>
      <RedesignImage
        alt=""
        src={`${publicIconPath}/${showPassword ? 'EyeOpened.svg' : 'EyeClosed.svg'}`}
      />
    </button>
  );

  const renderStepIndicator = () => (
    <div className={cls.stepIndicator}>
      {step === 1 && (
        <p className={cls.stepLink}>
          Vous avez déjà un compte ?{' '}
          <button
            type="button"
            className={cls.stepLinkBold}
            onClick={() => {
              setView('login');
              setErrors({});
            }}>
            Connectez-vous
          </button>
        </p>
      )}
      <div className={cls.steps}>
        <div className={cls.stepItem}>
          {step > 1 ? (
            <div className={cls.stepCheckDone}>&#10003;</div>
          ) : (
            <div className={cls.stepNumber}>1</div>
          )}
          <p className={step === 1 ? cls.stepLabelActive : cls.stepLabel}>Création compte</p>
        </div>
        <div className={cls.stepItem}>
          <div className={step === 2 ? cls.stepNumber : cls.stepNumberInactive}>2</div>
          <p className={step === 2 ? cls.stepLabelActive : cls.stepLabel}>
            Finaliser l&apos;inscription
          </p>
        </div>
      </div>
    </div>
  );

  const renderSsoButtons = (labels: { google: string; facebook: string; apple: string }) => (
    <div className={cls.ssoSection}>
      <button
        type="button"
        className={cls.outlineButton}
        onClick={() => {
          window.location.href = `${config.api_url}/api/connect/google`;
        }}>
        <RedesignImage src={`${publicIconPath}/Google.svg`} alt="" width={16} height={16} />
        <span className={cls.ssoButtonText}>{labels.google}</span>
      </button>
      <button
        type="button"
        className={cls.outlineButton}
        onClick={() => {
          window.location.href = `${config.api_url}/api/connect/facebook`;
        }}>
        <RedesignImage src={`${publicIconPath}/Facebook.svg`} alt="" width={16} height={16} />
        <span className={cls.ssoButtonText}>{labels.facebook}</span>
      </button>
      <button
        type="button"
        className={cls.outlineButton}
        onClick={() => {
          window.location.href = getAppleConnectUrl();
        }}>
        <RedesignImage src={`${publicIconPath}/Apple.svg`} alt="" width={16} height={16} />
        <span className={cls.ssoButtonText}>{labels.apple}</span>
      </button>
    </div>
  );

  if (showAccountConfirm) {
    return (
      <div className={cls.confirmCard}>
        <AccountConfirm
          title="Confirmation"
          email={form.email || defaultEmail}
          onRequestSms={step2.phone ? handleRequestSms : undefined}
          onNeedHelp={() => setHelpOpen(true)}
        />
        {smsError && <p className={cls.smsErrorText}>{smsError}</p>}
        <HelpModal
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          defaultEmail={form.email || defaultEmail}
        />
      </div>
    );
  }

  if (resultId) {
    return (
      <div className={cls.resultCard}>
        <div className={cls.resultIconWrapper}>
          <CheckRounded className={cls.resultIcon} />
        </div>
        <h2 className={cls.resultTitle}>Résultats</h2>
        <p className={cls.resultDescription}>
          Vous allez découvrir maintenant votre reconnaissance monétaire privilégiée.
        </p>
        <Button
          className={cls.resultButton}
          onClick={() => history.push(`/congrats/${resultId}`)}
          endIcon={<ArrowForward />}>
          Voir les résultats
        </Button>
      </div>
    );
  }

  return (
    <div className={cls.card}>
      <div className={cls.layout}>
        <div className={cls.imageWrapper}>
          <RedesignImage
            src="/images/MinorTest/test-share.png"
            alt={isEbook ? 'E-book' : 'Test'}
            className={cls.image}
          />
        </div>
        <div className={cls.content}>
          <div style={view !== 'login' ? { display: 'none' } : undefined}>
            <h2 className={cls.title}>
              {isEbook
                ? 'Poursuivre votre ebook'
                : isGame
                ? 'Poursuivre votre serious games'
                : 'Accédez à vos résultats'}
            </h2>
            <p className={cls.description}>
              {isEbook
                ? 'Créez votre compte pour enregistrer votre progression et poursuivre le ebook'
                : isGame
                ? 'Créez votre compte pour enregistrer votre progression et poursuivre le serious games'
                : 'Connectez-vous pour enregistrer votre progression et poursuivre le test.'}
            </p>

            <label className={cls.field} htmlFor="inline-login-email">
              <span className={cls.label}>Email</span>
              <div className={cls.inputWrapper}>
                <input
                  id="inline-login-email"
                  className={`${cls.input} ${cls.inputWithIcon}`}
                  type="email"
                  placeholder={loginLocale.emailPlaceholder}
                  value={loginForm.email}
                  onChange={e => {
                    setLoginForm({ ...loginForm, email: e.target.value });
                    setLoginErrors({ ...loginErrors, email: '' });
                  }}
                />
                <RedesignImage
                  src={`${publicIconPath}/Email.svg`}
                  alt=""
                  className={cls.inputIcon}
                  style={{ cursor: 'default' }}
                />
              </div>
              {loginErrors.email && <span className={cls.errorText}>{loginErrors.email}</span>}
            </label>

            <label className={cls.field} htmlFor="inline-login-password">
              <span className={cls.label}>Mot de passe</span>
              <div className={cls.inputWrapper}>
                <input
                  id="inline-login-password"
                  className={`${cls.input} ${cls.inputWithIcon}`}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={loginLocale.passwordPlaceholder}
                  value={loginForm.password}
                  onChange={e => {
                    setLoginForm({ ...loginForm, password: e.target.value });
                    setLoginErrors({ ...loginErrors, password: '' });
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleLoginSubmit();
                  }}
                />
                {renderPasswordToggle()}
              </div>
              {loginErrors.password && (
                <span className={cls.errorText}>{loginErrors.password}</span>
              )}
            </label>

            <Link to="/forgot-password" className={cls.forgotPassword}>
              Mot de passe oublié ?
            </Link>

            {loginErrors.global && <p className={cls.errorText}>{loginErrors.global}</p>}

            <Button
              className={cls.submitButton}
              onClick={handleLoginSubmit}
              disabled={isLoading}
              fullWidth={true}>
              {isLoading ? 'Connexion...' : 'Connexion'}
            </Button>

            <div className={cls.dividerRow}>
              <div className={cls.dividerLine} />
              <span className={cls.dividerText}>ou</span>
              <div className={cls.dividerLine} />
            </div>

            {renderSsoButtons({
              google: loginLocale.ssoGoogle,
              facebook: loginLocale.ssoFacebook,
              apple: loginLocale.ssoApple,
            })}

            <p className={cls.bottomLink}>
              Pas encore de compte ?{' '}
              <button
                type="button"
                className={cls.stepLinkBold}
                onClick={() => {
                  setView('signup');
                  setLoginErrors({});
                }}>
                Inscrivez-vous
              </button>
            </p>
          </div>

          <div style={view !== 'signup' || step !== 1 ? { display: 'none' } : undefined}>
            <h2 className={cls.title}>
              {isEbook
                ? 'Poursuivre votre ebook'
                : isGame
                ? 'Poursuivre votre serious games'
                : 'Accédez à vos résultats'}
            </h2>
            <p className={cls.description}>
              {isEbook
                ? 'Créez votre compte pour enregistrer votre progression et poursuivre le ebook'
                : isGame
                ? 'Créez votre compte pour enregistrer votre progression et poursuivre le serious games'
                : 'Connectez-vous pour enregistrer votre progression et poursuivre le test.'}
            </p>
            {renderStepIndicator()}

            <label className={cls.field} htmlFor="inline-signup-lastname">
              <span className={cls.label}>Nom</span>
              <input
                id="inline-signup-lastname"
                className={cls.input}
                type="text"
                placeholder={signupLocale.lastnamePlaceholder}
                value={form.lastname}
                onChange={handleChange('lastname')}
              />
              {errors.lastname && <span className={cls.errorText}>{errors.lastname}</span>}
            </label>

            <label className={cls.field} htmlFor="inline-signup-firstname">
              <span className={cls.label}>Prénom</span>
              <input
                id="inline-signup-firstname"
                className={cls.input}
                type="text"
                placeholder={signupLocale.firstnamePlaceholder}
                value={form.firstname}
                onChange={handleChange('firstname')}
              />
              {errors.firstname && <span className={cls.errorText}>{errors.firstname}</span>}
            </label>

            <label className={cls.field} htmlFor="inline-signup-email">
              <span className={cls.label}>Email</span>
              <div className={cls.inputWrapper}>
                <input
                  id="inline-signup-email"
                  className={`${cls.input} ${cls.inputWithIcon}`}
                  type="email"
                  placeholder={signupLocale.emailPlaceholder}
                  value={form.email}
                  onChange={handleChange('email')}
                />
                <RedesignImage
                  src={`${publicIconPath}/Email.svg`}
                  alt=""
                  className={cls.inputIcon}
                  style={{ cursor: 'default' }}
                />
              </div>
              <p className={cls.helperText}>{signupLocale.emailHelperText}</p>
              {errors.email && <span className={cls.errorText}>{errors.email}</span>}
            </label>

            <label className={cls.field} htmlFor="inline-signup-password">
              <span className={cls.label}>Mot de passe</span>
              <div className={cls.inputWrapper}>
                <input
                  id="inline-signup-password"
                  className={`${cls.input} ${cls.inputWithIcon}`}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={signupLocale.passwordPlaceholder}
                  value={form.password}
                  onChange={handleChange('password')}
                />
                {renderPasswordToggle()}
              </div>
              {errors.password && <span className={cls.errorText}>{errors.password}</span>}
            </label>

            <label className={cls.field} htmlFor="inline-signup-confirm-password">
              <span className={cls.label}>Confirmation du mot de passe</span>
              <div className={cls.inputWrapper}>
                <input
                  id="inline-signup-confirm-password"
                  className={`${cls.input} ${cls.inputWithIcon}`}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={signupLocale.confirmPasswordPlaceholder}
                  value={form.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                />
                {renderPasswordToggle()}
              </div>
              {errors.confirmPassword && (
                <span className={cls.errorText}>{errors.confirmPassword}</span>
              )}
            </label>

            <div className={cls.checkboxRow}>
              <Checkbox
                checked={form.cgu}
                onChange={e => setForm({ ...form, cgu: e.target.checked })}
                className={cls.checkbox}
                color="primary"
                id="inline-signup-cgu"
              />
              <label htmlFor="inline-signup-cgu">
                <p className={cls.checkboxLabel}>
                  J&apos;ai lu et j&apos;accepte les{' '}
                  <Link
                    to="../../../../../orientaction-cgu.pdf"
                    target="_blank"
                    className={cls.linkPrimary}>
                    Conditions générales d&apos;utilisation
                  </Link>{' '}
                  et la{' '}
                  <Link
                    to="../../../../../orientaction-pdc.pdf"
                    target="_blank"
                    className={cls.linkPrimary}>
                    Politique de confidentialité
                  </Link>
                </p>
              </label>
            </div>
            {errors.cgu && <span className={cls.errorText}>{errors.cgu}</span>}

            {step === 1 && (
              <div className={cls.field}>
                <TurnstileWidget {...turnstile.widgetProps} error={turnstile.error} />
              </div>
            )}

            <Button
              className={cls.submitButton}
              onClick={handleStep1Submit}
              disabled={isLoading}
              fullWidth={true}>
              {isLoading ? 'Suivant...' : 'Suivant'}
            </Button>

            <div className={cls.dividerRow}>
              <div className={cls.dividerLine} />
              <span className={cls.dividerText}>ou</span>
              <div className={cls.dividerLine} />
            </div>

            {renderSsoButtons({
              google: signupLocale.ssoGoogle,
              facebook: signupLocale.ssoFacebook,
              apple: signupLocale.ssoApple,
            })}
          </div>

          <div style={view !== 'signup' || step !== 2 ? { display: 'none' } : undefined}>
            <h2 className={cls.title}>Finalisez votre inscription</h2>
            <p className={cls.description}>
              Merci de nous indiquer votre numéro de téléphone et votre code postal qui ne seront
              utilisés que par les équipes Orientaction.
            </p>
            {renderStepIndicator()}

            <div className={cls.field}>
              <span className={cls.label}>Téléphone</span>
              <MaterialPhoneInput
                value={step2.phone as any}
                onChange={(val: any) => {
                  setStep2({ ...step2, phone: val || '' });
                  setErrors({ ...errors, phone: '' });
                }}
                error={!!errors.phone}
                className={cls.materialPhoneInput}
              />
              {errors.phone && <span className={cls.errorText}>{errors.phone}</span>}
            </div>

            <label className={cls.field} htmlFor="inline-signup-postal-code">
              <span className={cls.label}>Code Postal</span>
              <input
                id="inline-signup-postal-code"
                className={cls.input}
                type="text"
                placeholder={signupLocale.postalCodePlaceholder}
                value={step2.postal_code}
                onChange={e => {
                  setStep2({ ...step2, postal_code: e.target.value });
                  setErrors({ ...errors, postal_code: '' });
                }}
              />
              {errors.postal_code && <span className={cls.errorText}>{errors.postal_code}</span>}
            </label>

            {errors.global && <p className={cls.errorText}>{errors.global}</p>}

            {step === 2 && (
              <div className={cls.field}>
                <TurnstileWidget {...turnstile.widgetProps} error={turnstile.error} />
              </div>
            )}

            <Button
              className={cls.submitButton}
              onClick={handleStep2Submit}
              disabled={isLoading}
              fullWidth={true}>
              {isLoading ? 'Création en cours...' : 'Créer mon compte'}
            </Button>

            <button
              type="button"
              className={cls.backButton}
              onClick={() => {
                setStep(1);
                setErrors({});
                turnstile.reset();
              }}>
              <RedesignImage
                src={`${publicIconPath}/ArrowLeft.svg`}
                alt=""
                width={16}
                height={16}
              />
              <span>Retour</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InlineSignup;
